/**
 * 用量反応（LD50 / EC50 / EC90 / TCID50）専用 3列コンパクト表
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  doseResponseColumnFormToGridData,
  gridDataToDoseResponseColumnForm,
  type DoseResponseColumnForm,
  normalizeKeisukeNumericInput,
} from '../../../../utils/keisukeInputFormUtils';
import type { DataRow } from '../../../../stores/systems/keisuke/dashboardStore';
import {
  clipboardLooksLikeSpreadsheetGrid,
  clipboardPlainTextFromEvent,
  parseClipboardTsvMatrix,
} from './keisukeDashboardClipboardUtils';
import { useDebouncedFormApply } from './useDebouncedFormApply';
import { useKeisukeThreeColumnGridClipboard } from './useKeisukeThreeColumnGridClipboard';
import {
  emptyThreeColumnRows,
  resizeThreeColumnRows,
  useKeisukeCompactFormResetOnNew,
} from './keisukeThreeColumnFormHelpers';
import styles from './KeisukeDashboardCenterPane.module.scss';
import grid from './KeisukeUnifiedDataGrid.module.scss';
import KeisukeDashboardGridObservationCountRow from './KeisukeDashboardGridObservationCountRow';
import KeisukeClipboardHintBar from './KeisukeClipboardHintBar';

export interface KeisukeDashboardDataFormDoseResponseProps {
  gridData: DataRow[];
  gridHeaders: string[];
  doseColumn?: string;
  effectColumn?: string;
  totalColumn?: string;
  onApply: (data: DataRow[], headers: string[]) => void;
  disabled?: boolean;
  nRows: number;
  onNRowsChange: (n: number) => void;
}

export default function KeisukeDashboardDataFormDoseResponse({
  gridData,
  gridHeaders,
  doseColumn,
  effectColumn,
  totalColumn,
  onApply,
  disabled = false,
  nRows,
  onNRowsChange,
}: KeisukeDashboardDataFormDoseResponseProps) {
  const { t } = useTranslation();
  const fieldIdPrefix = useId();
  const skipSyncRef = useRef(false);
  const suppressUntilRef = useRef(0);

  const buildEmptyForm = useCallback(
    (): DoseResponseColumnForm => ({
      doseHeader: t('keisuke.dashboard.inputForm.compactDefaultDose'),
      effectHeader: t('keisuke.dashboard.inputForm.compactDefaultEffect'),
      totalHeader: t('keisuke.dashboard.inputForm.compactDefaultTotal'),
      valueRows: emptyThreeColumnRows(nRows),
    }),
    [t, nRows]
  );

  const [form, setForm] = useState<DoseResponseColumnForm>(() => {
    const fromGrid = gridDataToDoseResponseColumnForm(
      gridData,
      gridHeaders,
      doseColumn,
      effectColumn,
      totalColumn
    );
    if (fromGrid) return fromGrid;
    /** 空表は i18n 既定ヘッダのみ。前解析の mapping（group/value 等）で汚さない */
    return {
      doseHeader: t('keisuke.dashboard.inputForm.compactDefaultDose'),
      effectHeader: t('keisuke.dashboard.inputForm.compactDefaultEffect'),
      totalHeader: t('keisuke.dashboard.inputForm.compactDefaultTotal'),
      valueRows: emptyThreeColumnRows(nRows),
    };
  });

  const clipboard = useKeisukeThreeColumnGridClipboard(form.valueRows, disabled);

  const prevNRef = useRef(nRows);
  useEffect(() => {
    if (prevNRef.current === nRows) return;
    prevNRef.current = nRows;
    setForm((prev) => ({ ...prev, valueRows: resizeThreeColumnRows(prev.valueRows, nRows) }));
  }, [nRows]);

  useEffect(() => {
    if (Date.now() < suppressUntilRef.current) return;
    if (skipSyncRef.current) {
      skipSyncRef.current = false;
      return;
    }
    const fromGrid = gridDataToDoseResponseColumnForm(
      gridData,
      gridHeaders,
      doseColumn,
      effectColumn,
      totalColumn
    );
    if (fromGrid) {
      setForm(fromGrid);
      if (fromGrid.valueRows.length !== nRows) onNRowsChange(fromGrid.valueRows.length);
      return;
    }
    /** 新規などで headers が空のとき fromGrid は null → ローカル表を残さない */
    if (gridHeaders.length === 0) {
      setForm(buildEmptyForm());
    }
  }, [
    gridData,
    gridHeaders,
    doseColumn,
    effectColumn,
    totalColumn,
    onNRowsChange,
    nRows,
    buildEmptyForm,
  ]);

  const resetToEmpty = useCallback(() => setForm(buildEmptyForm()), [buildEmptyForm]);
  useKeisukeCompactFormResetOnNew(resetToEmpty);

  const applyForm = useCallback(() => {
    const { headers, data } = doseResponseColumnFormToGridData(form);
    suppressUntilRef.current = Date.now() + 950;
    skipSyncRef.current = true;
    onApply(data, headers);
  }, [form, onApply]);

  useDebouncedFormApply(applyForm, [form, applyForm], { delayMs: 280, skipInitial: true });

  const onPaste = useCallback(
    (e: React.ClipboardEvent, startRow: number, startCol: number) => {
      if (disabled) return;
      const text = clipboardPlainTextFromEvent(e);
      if (!clipboardLooksLikeSpreadsheetGrid(text)) return;
      e.preventDefault();
      const matrix = parseClipboardTsvMatrix(text);
      if (matrix.length === 0) return;
      setForm((prev) => {
        const nextRows = prev.valueRows.map((r) => [...r] as [string, string, string]);
        if (startRow < 0) {
          const headerLine = matrix[0] ?? [];
          const doseHeader = headerLine[0] ?? prev.doseHeader;
          const effectHeader = headerLine[1] ?? prev.effectHeader;
          const totalHeader = headerLine[2] ?? prev.totalHeader;
          const dataMatrix = matrix.slice(1);
          const merged = { ...prev, doseHeader, effectHeader, totalHeader };
          for (let i = 0; i < dataMatrix.length; i++) {
            const r = i;
            while (nextRows.length <= r) nextRows.push(['', '', '']);
            const line = dataMatrix[i] ?? [];
            for (let j = 0; j < line.length && j < 3; j++) {
              nextRows[r]![startCol + j] = normalizeKeisukeNumericInput(String(line[j] ?? ''));
            }
          }
          const out = { ...merged, valueRows: nextRows };
          if (out.valueRows.length > nRows) queueMicrotask(() => onNRowsChange(out.valueRows.length));
          queueMicrotask(applyForm);
          return out;
        }
        for (let i = 0; i < matrix.length; i++) {
          const r = startRow + i;
          while (nextRows.length <= r) nextRows.push(['', '', '']);
          const line = matrix[i] ?? [];
          for (let j = 0; j < line.length && startCol + j < 3; j++) {
            nextRows[r]![startCol + j] = normalizeKeisukeNumericInput(String(line[j] ?? ''));
          }
        }
        const minLen = Math.max(nRows, startRow + matrix.length);
        while (nextRows.length < minLen) nextRows.push(['', '', '']);
        const out = { ...prev, valueRows: nextRows };
        if (out.valueRows.length > nRows) queueMicrotask(() => onNRowsChange(out.valueRows.length));
        queueMicrotask(applyForm);
        return out;
      });
    },
    [disabled, nRows, onNRowsChange, applyForm]
  );

  const setHeader = (key: 'doseHeader' | 'effectHeader' | 'totalHeader', v: string) =>
    setForm((prev) => ({ ...prev, [key]: v }));

  const setCell = (row: number, col: 0 | 1 | 2, value: string) => {
    const v = normalizeKeisukeNumericInput(value);
    setForm((prev) => {
      const rows = prev.valueRows.map((line, ri) =>
        ri === row
          ? ([
              col === 0 ? v : line[0],
              col === 1 ? v : line[1],
              col === 2 ? v : line[2],
            ] as [string, string, string])
          : line
      );
      return { ...prev, valueRows: rows };
    });
  };

  const headerLabels = [
    { key: 'doseHeader' as const, val: form.doseHeader, col: 0 as const },
    { key: 'effectHeader' as const, val: form.effectHeader, col: 1 as const },
    { key: 'totalHeader' as const, val: form.totalHeader, col: 2 as const },
  ];

  return (
    <div className={`${styles.formWrap} ${styles.formWrapDense}`}>
      <KeisukeDashboardGridObservationCountRow
        label={t('keisuke.dashboard.inputForm.doseResponseNRows')}
        value={nRows}
        onChange={onNRowsChange}
        onBlur={applyForm}
        disabled={disabled}
        min={2}
        max={30}
      />
      <div className={grid.keisukeAnovaTableWrap}>
        <div className={grid.tableScroll}>
          <table
            ref={clipboard.tableRef}
            className={grid.keisukeAnovaGridTable}
            onPointerDownCapture={clipboard.onTablePointerDownCapture}
            onCopyCapture={clipboard.onTableCopyCapture}
            onKeyDownCapture={clipboard.onTableKeyDownCapture}
          >
            <colgroup>
              <col className={grid.keisukeRowNumCol} />
              <col className={grid.keisukeAnovaColData} />
              <col className={grid.keisukeAnovaColData} />
              <col className={grid.keisukeAnovaColData} />
            </colgroup>
            <thead>
              <tr>
                <th className={grid.keisukeRowNumCell} aria-hidden>#</th>
                {headerLabels.map(({ key, val, col }) => (
                  <th key={key}>
                    <input
                      type="text"
                      className={grid.keisukeAnovaGridInputHeader}
                      id={`${fieldIdPrefix}-dr-h-${col}`}
                      value={val}
                      disabled={disabled}
                      onChange={(e) => setHeader(key, e.target.value)}
                      onBlur={applyForm}
                      onPaste={(e) => onPaste(e, -1, col)}
                      aria-label={t('keisuke.dashboard.columnNameAria', { column: val })}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {form.valueRows.map((row, ri) => (
                <tr key={ri}>
                  <td className={grid.keisukeRowNumCell} aria-hidden>{ri + 1}</td>
                  {([0, 1, 2] as const).map((ci) => (
                    <td key={ci} className={clipboard.gridCellClass(ri, ci)}>
                      <input
                        type="text"
                        className={grid.keisukeAnovaGridInputCell}
                        id={`${fieldIdPrefix}-dr-r${ri}-c${ci}`}
                        value={row[ci]}
                        disabled={disabled}
                        inputMode="decimal"
                        onChange={(e) => setCell(ri, ci, e.target.value)}
                        onBlur={applyForm}
                        onPaste={(e) => onPaste(e, ri, ci)}
                        aria-label={`${headerLabels[ci]!.val} ${ri + 1}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <KeisukeClipboardHintBar
        visible={form.valueRows.some((row) => row.some((c) => String(c).trim() !== ''))}
        trailingHint={t('keisuke.dashboard.inputForm.doseResponseCompactHint')}
      />
    </div>
  );
}
